import {
  mapPointsInvoiceDto,
  mapPointsOrderDto,
  mapPointsOrderPageDto,
  mapPointsPackageDto,
  mapPointsRefundDto,
  mapPointsRefundQuoteDto,
  type PointsInvoiceDto,
  type PointsOrderDto,
  type PointsOrderPageDto,
  type PointsPackageDto,
  type PointsRefundDto,
  type PointsRefundQuoteDto,
  type PointsStatusDto,
} from '@/types/points';

import type { PointsService } from '../pointsService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`PointsService`(Creative BE docs/141 v2.1;`/api/wallet/mentor/*`)
 * -----------------------------------------------------------------------------
 * - 工作區範圍:transport 自動帶 `X-Current-Group`(M2 / M3 / M4 需要;單筆端點 BE 以 ref 驗本人)。
 * - 冪等鍵走 **body**(`order_ref` / `refund_id`),不是 header。
 * - 路徑參數一律 `encodeURIComponent`(值來自網址參數);子路徑用 dash(`pay-url` / `invoice-link` / `refund-quote`)。
 * - 清單類回應包一層 `result`(M1 `{result: Package[]}`、M4 `{result: Order[], next_cursor, summary}`)。
 */

const BASE = '/api/wallet/mentor';

function pageQuery(q?: { cursor?: string | null; limit?: number }): string {
  const params = new URLSearchParams();
  if (q?.limit !== undefined) params.set('limit', String(q.limit));
  if (q?.cursor) params.set('cursor', q.cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

const PATH = {
  packages: `${BASE}/packages`,
  status: `${BASE}/status`,
  orders: (q?: { cursor?: string | null; limit?: number }) => `${BASE}/orders${pageQuery(q)}`,
  order: (ref: string, refresh?: boolean) =>
    `${BASE}/orders/${encodeURIComponent(ref)}${refresh ? '?refresh=true' : ''}`,
  payUrl: (ref: string) => `${BASE}/orders/${encodeURIComponent(ref)}/pay-url`,
  invoiceLink: (ref: string) => `${BASE}/orders/${encodeURIComponent(ref)}/invoice-link`,
  refundQuote: (ref: string) => `${BASE}/orders/${encodeURIComponent(ref)}/refund-quote`,
  refunds: (ref: string) => `${BASE}/orders/${encodeURIComponent(ref)}/refunds`,
  refund: (id: string, refresh?: boolean) =>
    `${BASE}/refunds/${encodeURIComponent(id)}${refresh ? '?refresh=true' : ''}`,
} as const;

export function createHttpPointsService(config: HttpClientConfig): PointsService {
  return {
    async listPackages() {
      const env = await httpRequestWithRefresh<{ result: PointsPackageDto[] }>(config, 'GET', PATH.packages);
      return (unwrapResult(env, 'points.listPackages').result ?? []).map(mapPointsPackageDto);
    },

    async getStatus() {
      const env = await httpRequestWithRefresh<PointsStatusDto>(config, 'GET', PATH.status);
      return { configured: unwrapResult(env, 'points.getStatus').configured === true };
    },

    async createOrder(req) {
      const env = await httpRequestWithRefresh<PointsOrderDto>(config, 'POST', PATH.orders(), {
        package_code: req.packageCode,
        order_ref: req.orderRef,
        return_url: req.returnUrl,
      });
      return mapPointsOrderDto(unwrapResult(env, 'points.createOrder'));
    },

    async listOrders(q) {
      const env = await httpRequestWithRefresh<PointsOrderPageDto>(config, 'GET', PATH.orders(q));
      return mapPointsOrderPageDto(unwrapResult(env, 'points.listOrders'));
    },

    async getOrder(orderRef, opts) {
      const env = await httpRequestWithRefresh<PointsOrderDto>(config, 'GET', PATH.order(orderRef, opts?.refresh));
      return mapPointsOrderDto(unwrapResult(env, 'points.getOrder'));
    },

    async getPayUrl(orderRef) {
      const env = await httpRequestWithRefresh<{ pay_url: string }>(config, 'GET', PATH.payUrl(orderRef));
      return unwrapResult(env, 'points.getPayUrl').pay_url;
    },

    async getInvoice(orderRef) {
      const env = await httpRequestWithRefresh<PointsInvoiceDto>(config, 'GET', PATH.invoiceLink(orderRef));
      return mapPointsInvoiceDto(unwrapResult(env, 'points.getInvoice'));
    },

    async getRefundQuote(orderRef) {
      const env = await httpRequestWithRefresh<PointsRefundQuoteDto>(config, 'GET', PATH.refundQuote(orderRef));
      return mapPointsRefundQuoteDto(unwrapResult(env, 'points.getRefundQuote'));
    },

    async requestRefund(orderRef, req) {
      const env = await httpRequestWithRefresh<PointsRefundDto>(config, 'POST', PATH.refunds(orderRef), {
        refund_id: req.refundId,
        quoted_amount: req.quotedAmount,
      });
      return mapPointsRefundDto(unwrapResult(env, 'points.requestRefund'));
    },

    async getRefund(refundId, opts) {
      const env = await httpRequestWithRefresh<PointsRefundDto>(config, 'GET', PATH.refund(refundId, opts?.refresh));
      return mapPointsRefundDto(unwrapResult(env, 'points.getRefund'));
    },
  };
}
